// Account self-service: password reset, profile, change password
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getErrorMessage } from '../utils/apiError';

const fail = (error, fallback) => ({
  success: false,
  status: error?.response?.status,
  error: getErrorMessage(error, fallback),
});

const accountService = {
  // Sends a 6-digit code to the account's email. The response is the same whether or not the account exists.
  requestPasswordReset: async identifier => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { identifier });
      return { success: true, message: response.data?.message };
    } catch (error) {
      return fail(error, 'Could not send the reset code');
    }
  },

  // identifier = the email or phone used to request the code
  resetPassword: async ({ identifier, code, newPassword }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, { identifier, code, newPassword });
      return { success: true, message: response.data?.message };
    } catch (error) {
      return fail(error, 'Could not reset the password');
    }
  },

  // Emails the signed-in user a new verification link (max 3 an hour).
  sendVerificationEmail: async () => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USERS.SEND_VERIFICATION);
      return { success: true, message: response.data?.message };
    } catch (error) {
      return fail(error, "Couldn't send the verification email");
    }
  },

  // From the emailed link (/verify-email?uid&token). Works without signing in.
  verifyEmail: async ({ userId, token }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, { userId, token });
      return { success: true, data: response.data?.data, message: response.data?.message };
    } catch (error) {
      return fail(error, "We couldn't verify your email. Request a new link from your account.");
    }
  },

  // { id, fullName, email, emailConfirmed, phone, gitHubUsername, role, status, createdDate, … }
  getMe: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USERS.ME);
      return { success: true, data: response.data?.data };
    } catch (error) {
      return fail(error, 'Could not load your profile');
    }
  },

  // Any of fullName, email, gitHubUsername. Changing the GitHub username moves repository access to the new account.
  updateMe: async ({ fullName, email, gitHubUsername }) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.USERS.ME, { fullName, email, gitHubUsername });
      return { success: true, data: response.data?.data, message: response.data?.message };
    } catch (error) {
      return fail(error, 'Could not update your profile');
    }
  },

  changePassword: async ({ currentPassword, newPassword, confirmNewPassword }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USERS.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
        confirmNewPassword,
      });
      return { success: true, message: response.data?.message };
    } catch (error) {
      return fail(error, 'Could not change the password');
    }
  },
};

export default accountService;
